import { api } from "@/api";
import { SubscribeRequestType } from "@/api/Api";
import {
  subscribeWithRecovery,
  type SubscriptionLifecycle,
} from "@/util/pushSubscription";

/**
 * Converts a base64 VAPID key to ArrayBuffer for Push API
 * Some TS DOM lib versions require ArrayBuffer (not ArrayBufferLike) for BufferSource.
 */
function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const arrayBuffer = new ArrayBuffer(rawData.length);
  const view = new Uint8Array(arrayBuffer);
  for (let i = 0; i < rawData.length; ++i) {
    view[i] = rawData.charCodeAt(i);
  }
  return arrayBuffer;
}

/**
 * Registers the service worker for push notifications
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service Worker not supported");
  }

  const registration = await navigator.serviceWorker.register("/sw.js");

  // Check for updates immediately
  registration.update();

  // Listen for updates
  registration.addEventListener("updatefound", () => {
    const newWorker = registration.installing;
    if (newWorker) {
      newWorker.addEventListener("statechange", () => {
        if (newWorker.state === "activated") {
          console.log("Service Worker updated and activated");
        }
      });
    }
  });

  await navigator.serviceWorker.ready;
  return registration;
}

/**
 * Requests notification permission from the user
 */
async function requestNotificationPermission(): Promise<boolean> {
  if (!("Notification" in window)) {
    throw new Error("Notifications not supported");
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission === "denied") {
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === "granted";
}

/**
 * Subscribes to web push notifications
 * This function handles the complete flow:
 * 1. Request notification permission
 * 2. Register service worker
 * 3. Get VAPID key from server
 * 4. Subscribe to push notifications (with recovery)
 * 5. Send subscription to backend
 */
export async function subscribeToWebPush(): Promise<void> {
  try {
    // Check browser support
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.log("Web Push not supported in this browser");
      return;
    }

    // Check if already subscribed
    const alreadySubscribed = await isSubscribedToWebPush();
    if (alreadySubscribed) {
      console.log("Already subscribed to web push notifications");
      return;
    }

    // 1. Request permission (only if not already granted)
    if (Notification.permission !== "granted") {
      const granted = await requestNotificationPermission();
      if (!granted) {
        console.log("Notification permission denied by user");
        return;
      }
    }

    // 2. Register service worker
    const registration = await registerServiceWorker();

    // 3. Get VAPID public key (after permission to avoid unnecessary network calls)
    const { data: vapidData } = await api.notification.getVapidPublicKey();
    if (!vapidData.publicKey) {
      throw new Error("Failed to get VAPID public key");
    }

    const subscriptionLifecycle: SubscriptionLifecycle<PushSubscription> = {
      subscribe: (options) => registration.pushManager.subscribe(options),
      getSubscription: () => registration.pushManager.getSubscription(),
      unsubscribe: async (existing) => {
        await existing.unsubscribe();
      },
    };

    // 4. Subscribe to push manager with recovery when VAPID key rotates
    const subscription = await subscribeWithRecovery(subscriptionLifecycle, {
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToArrayBuffer(vapidData.publicKey),
    });

    // 5. Send subscription to backend
    await api.notification.subscribe({
      type: SubscribeRequestType.WEB_PUSH,
      data: JSON.stringify(subscription.toJSON()),
    });

    console.log("Successfully subscribed to web push notifications");
  } catch {
    console.error("Failed to subscribe to web push");
    // Don't throw - we don't want to break the user flow if push subscription fails
  }
}

/**
 * Checks if the user is already subscribed to push notifications
 * Validates both client-side subscription and server-side subscription
 */
export async function isSubscribedToWebPush(): Promise<boolean> {
  try {
    if (!("serviceWorker" in navigator)) {
      return false;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return false;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      return false;
    }

    // Check server-side subscription
    try {
      const { data: serverSubscription } =
        await api.notification.getSubscription();
      // If server doesn't have a valid subscription (no id), need to resubscribe
      if (!serverSubscription.id) {
        return false;
      }
    } catch (error) {
      // If server request fails (e.g., 404 not found), need to resubscribe
      console.log("Server subscription not found, will resubscribe", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Failed to check push subscription status:", error);
    return false;
  }
}
